import isEmpty from 'lodash/isEmpty';
import isArray from 'lodash/isArray';
import isNil from 'lodash/isNil';
import isString from 'lodash/isString';
import omitBy from 'lodash/omitBy';
import qs from 'qs';
import { getAccessToken as getLegacyAccessToken } from '@/legacy';
import { getCookie } from './cookie';
import { FetchError, FetchResponse } from './types';
import jotaiInMemoryStorage from '@/states/observer';

export type { FetchError, FetchResponse };

let GATEWAY_URL: string;

export type RequestConfig<RequestBody, RequestQuery> = {
  request?: RequestBody;
  query?: RequestQuery;
  header?: Record<string, string>; // sorry, this is silly
  headers?: Record<string, string>; // let's use this one
  public?: boolean;
  arrayFormat?: 'indices' | 'brackets' | 'repeat' | 'comma';
};

type FetchApiV2Params<RequestBody, RequestQuery> = {
  method: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
  url: string;
  reqConfig?: RequestConfig<RequestBody, RequestQuery>;
};

let gatewayUrlResolver: (
  params: FetchApiV2Params<any, any>
) => string | undefined;

const resetLastAction = () => {
  localStorage.setItem('LAST_ACTION', Date.now().toString());
};

type InitConfig = {
  gatewayUrl: string; // the default domain of the gateway
  gatewayUrlResolver?: (
    params: FetchApiV2Params<any, any>
  ) => string | undefined; // in case of IM dashboard, it has 2 gateways, hence this is needed
};

export const init = ({
  gatewayUrl,
  gatewayUrlResolver: resolver
}: InitConfig) => {
  if (!GATEWAY_URL) {
    GATEWAY_URL = gatewayUrl;
  } else {
    console.warn(`
GATEWAY_URL is already initialized. You should not call init() twice.
If you are in a micro-frontend:
  1. Check your lib version
  2. Make sure you add the lib to the shared lib in Module Federation configuration
  3. The lib export a backward compatible version of fetchAPI, but you should refactor to use the new API (i.e. fetchApiV2)
`);
  }

  if (resolver) {
    console.log(`Gateway URL resolver is set`);
    gatewayUrlResolver = resolver;
  }
};

// const stSessionExists = async () => {
//   try {
//     const { doesSessionExist } = await import(
//       'supertokens-web-js/recipe/session'
//     );
//     return doesSessionExist();
//   } catch (error) {
//     console.error(error);
//     return false;
//   }
// };

const doesStCookieExist = () => {
  const stAcessToken = getCookie('st-access-token');
  return !!stAcessToken;
};

const envMap = {
  DEVELOPMENT: 'test',
  PRODUCTION: 'live'
};

export const getDGPEnv = (cookieEnv: string) => {
  const env =
    cookieEnv && cookieEnv.toLowerCase() === 'production'
      ? 'PRODUCTION'
      : 'DEVELOPMENT';
  return envMap[env] || envMap.DEVELOPMENT;
};

export const isDGPUrl = (url: string) => {
  const v2Regex = new RegExp('^/api/v2/.+');
  return v2Regex.test(url);
};

/**
 * @deprecated
 * Backwards compatible fetchAPI
 */
export const fetchAPI = async <
  RequestBody = Record<string, unknown>,
  RequestQuery = Record<string, unknown>,
  ResponseBody = FetchResponse
>(
  method: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH',
  domain: string,
  url: string,
  reqConfig?: RequestConfig<RequestBody, RequestQuery>
): Promise<ResponseBody & FetchResponse> => {
  init({ gatewayUrl: domain }); // Make the API backwards compatible
  return fetchApiV2<RequestBody, RequestQuery, ResponseBody>(
    method,
    url,
    reqConfig
  );
};

// This new API is the recommended way to use the fetch API
// it assumes the domain is initialized before calling the function,
// so the caller doesn't need to have the reference of the domain
export const fetchApiV2 = async <
  RequestBody = Record<string, unknown>,
  RequestQuery = Record<string, unknown>,
  ResponseBody = FetchResponse
>(
  method: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH',
  url: string,
  reqConfig?: RequestConfig<RequestBody, RequestQuery>
): Promise<ResponseBody & FetchResponse> => {
  const gatewayUrl = gatewayUrlResolver
    ? gatewayUrlResolver({ method, url, reqConfig }) || GATEWAY_URL
    : GATEWAY_URL;
  if (!gatewayUrl) {
    throw new Error(
      `gatewayUrl is not initialized
${method} ${url}
`
    );
  }

  let sessionExists = false;
  let accessToken;
  if (!reqConfig?.public) {
    // TODO: remove this after we fully migrate to the new auth
    accessToken = getLegacyAccessToken();
    if (!!accessToken) {
      sessionExists = true;
    } else {
      sessionExists = doesStCookieExist();
    }
    if (!sessionExists) {
      throw new Error('Session does not exist');
    }
  }
  resetLastAction();

  const environment = getCookie('environment') || 'DEVELOPMENT';
  const isDGP = isDGPUrl(url);

  // `qs` owns query-string encoding (array formats, %20); the URL object
  // owns structure and correct fragment placement.
  const queryString = qs.stringify(
    {
      ...(isDGP ? {} : { environment }),
      ...(reqConfig?.query
        ? omitBy(
            reqConfig.query,
            value =>
              isNil(value) ||
              ((isArray(value) || isString(value)) && isEmpty(value))
          )
        : {})
    },
    {
      ...(reqConfig?.arrayFormat && { arrayFormat: reqConfig.arrayFormat })
    }
  );
  const isFormData = reqConfig?.request instanceof FormData;

  // Skip SuperTokens interception when the new auth flow is off (so legacy
  // 401s aren't treated as expired sessions) or for `public` token-based
  // requests, which must never trigger a session refresh / UNAUTHORISED.
  const isNewAuthFlow = jotaiInMemoryStorage.getItem('isNewAuthFlow', false);
  const skipInterception = !isNewAuthFlow || !!reqConfig?.public;

  const builtUrl = new URL(url, gatewayUrl);
  if (queryString) {
    builtUrl.search = queryString;
  }
  if (skipInterception) {
    builtUrl.hash = 'superTokensDoNotDoInterception';
  }
  const fetchUrl = builtUrl.toString();

  const requestStart = Date.now();
  const response = await fetch(fetchUrl, {
    method,
    headers: {
      ...{ 'x-client-request-time': requestStart.toString() },
      ...(!isFormData && {
        'Content-Type': 'application/json'
      }),
      ...(!!accessToken && { Authorization: `Bearer ${accessToken}` }),
      ...(isDGP ? { mode: getDGPEnv(environment) } : {}),
      ...{ ...reqConfig?.headers, ...reqConfig?.header }
    },
    body: isFormData
      ? (reqConfig?.request as BodyInit)
      : JSON.stringify(reqConfig?.request)
  });

  if (!response.ok) {
    const contentType = response.headers.get('Content-Type');
    const headers = Array.from(response.headers.entries()).reduce(
      (acc, [key, value]) => {
        return {
          ...acc,
          [key]: value
        };
      },
      {}
    );
    const body: FetchError = {
      status: response.status,
      statusText: response.statusText,
      type: response.type,
      headers,
      data: contentType?.includes('application/json')
        ? await response.json()
        : contentType?.includes('text/')
          ? await response.text()
          : response.body
    };
    throw body;
  }

  const contentType = response.headers.get('Content-Type');
  let result: ResponseBody & FetchResponse;
  if (!contentType || contentType.includes('application/json')) {
    const text = await response.text();
    if (text.length === 0) return {} as ResponseBody & FetchResponse;
    try {
      result = JSON.parse(text);
      result._start = requestStart;
      result._end = Date.now();
      result._duration = Date.now() - requestStart;
      return result as ResponseBody & FetchResponse;
    } catch (error) {
      console.error('Failed to parse JSON response', error);
      return text as unknown as ResponseBody & FetchResponse;
    }
  }
  return (await response.text()) as unknown as ResponseBody & FetchResponse;
};
